// ============================================================================
// USE CONFIGURATION TEMPLATES HOOK
// ============================================================================

import { useState, useCallback } from 'react';
import {
  getConfigurationTemplatesSafe,
  getConfigurationTemplateSafe,
  createConfigurationTemplateSafe,
  updateConfigurationTemplateSafe,
  deleteConfigurationTemplateSafe,
  type ConfigurationTemplate,
  type CreateConfigurationTemplateRequest,
  type UpdateConfigurationTemplateRequest,
} from '../services/configurationTemplateManager';

interface UseConfigurationTemplatesReturn {
  templates: ConfigurationTemplate[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  getTemplate: (id: string | number) => Promise<ConfigurationTemplate | null>;
  createTemplate: (
    data: CreateConfigurationTemplateRequest
  ) => Promise<ConfigurationTemplate | null>;
  updateTemplate: (
    id: string | number,
    data: UpdateConfigurationTemplateRequest
  ) => Promise<ConfigurationTemplate | null>;
  deleteTemplate: (id: string | number) => Promise<boolean>;
}

/**
 * Hook for managing configuration templates
 * Provides loading states, error handling, and CRUD operations
 */
export const useConfigurationTemplates =
  (): UseConfigurationTemplatesReturn => {
    const [templates, setTemplates] = useState<ConfigurationTemplate[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const refetch = useCallback(async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await getConfigurationTemplatesSafe();
        setTemplates(data);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch templates';
        setError(errorMessage);
        console.error('Error fetching configuration templates:', err);
      } finally {
        setLoading(false);
      }
    }, []);

    const getTemplate = useCallback(
      async (id: string | number): Promise<ConfigurationTemplate | null> => {
        setLoading(true);
        setError(null);

        try {
          const template = await getConfigurationTemplateSafe(id);
          return template;
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to fetch template';
          setError(errorMessage);
          console.error(`Error fetching configuration template ${id}:`, err);
          return null;
        } finally {
          setLoading(false);
        }
      },
      []
    );

    const createTemplate = useCallback(
      async (
        data: CreateConfigurationTemplateRequest
      ): Promise<ConfigurationTemplate | null> => {
        setLoading(true);
        setError(null);

        try {
          const newTemplate = await createConfigurationTemplateSafe(data);
          if (newTemplate) {
            // Update local state by refetching
            await refetch();
          }
          return newTemplate;
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to create template';
          setError(errorMessage);
          console.error('Error creating configuration template:', err);
          throw err; // Re-throw to allow component-level error handling
        } finally {
          setLoading(false);
        }
      },
      [refetch]
    );

    const updateTemplate = useCallback(
      async (
        id: string | number,
        data: UpdateConfigurationTemplateRequest
      ): Promise<ConfigurationTemplate | null> => {
        setLoading(true);
        setError(null);

        try {
          const updatedTemplate = await updateConfigurationTemplateSafe(
            id,
            data
          );
          if (updatedTemplate) {
            // Update local state
            setTemplates(prev =>
              prev.map(t => (t.id === id ? updatedTemplate : t))
            );
          }
          return updatedTemplate;
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to update template';
          setError(errorMessage);
          console.error(`Error updating configuration template ${id}:`, err);
          throw err; // Re-throw to allow component-level error handling
        } finally {
          setLoading(false);
        }
      },
      []
    );

    const deleteTemplate = useCallback(
      async (id: string | number): Promise<boolean> => {
        setLoading(true);
        setError(null);

        try {
          const success = await deleteConfigurationTemplateSafe(id);
          if (success) {
            // Update local state
            setTemplates(prev => prev.filter(t => t.id !== id));
          }
          return success;
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to delete template';
          setError(errorMessage);
          console.error(`Error deleting configuration template ${id}:`, err);
          return false;
        } finally {
          setLoading(false);
        }
      },
      []
    );

    return {
      templates,
      loading,
      error,
      refetch,
      getTemplate,
      createTemplate,
      updateTemplate,
      deleteTemplate,
    };
  };
